/**
 * flowPreview.ts — the pure MODEL behind a tool's generated preview art.
 *
 * WHY THIS EXISTS: every tool-preview surface in the app is a placeholder (dashed boxes in the upload
 * wizard, an empty plate on the profile tiles, a grey rectangle in the search dropdown). There is no
 * image-upload route for tools and no static asset path. But the app already DERIVES a rich
 * structural description of every submitted automation — `api/dataverse/components.ts` walks the
 * blueprint into a `FlowComponentManifest`, and today that is only ever rendered as text. This module
 * turns the same manifest into a drawable model, so a reader can see the SHAPE of an automation (how
 * long it is, where it branches, how deep it nests, how many systems it touches) before reading a
 * word of it.
 *
 * WHY IT LIVES IN shared/ AND NOT IN web/: it is the same "one answer, both sides" rule that put
 * `flowComponents.ts` here. The upload wizard draws from the live manifest today; a catalogue tile
 * will eventually draw from an encoded shape string on the read payload. If each side derived
 * "what does this flow look like?" its own way, the same tool would render differently in the wizard
 * than on its own tile — and the wizard's promise ("this is your tool's tile") would be a lie.
 *
 * NOTHING HERE TOUCHES A BLUEPRINT, and nothing here reads author TEXT. `FlowShapeNode` is
 * deliberately narrower than `FlowComponent`: no `label`, no `typeLabel`, no `explanation`. The art
 * is drawn from structure and type only, so it cannot leak a step name into a decorative graphic that
 * ships to every viewer of a public catalogue. That narrowing is the disclosure boundary, and it is
 * enforced by the type rather than by a reviewer noticing.
 *
 * DETERMINISM IS THE CONTRACT. Every function here is a pure function of its inputs — the same tool
 * produces the same art in the wizard, on a tile, after a reload, and in another person's browser.
 * `Math.random()` and `Date.now()` must never appear in this file.
 *
 * Serves: the six placeholder slots recorded in docs/design-build/CARD_INVENTORY.md.
 */
import { FLOW_COMPONENT_KINDS } from './flowComponents.js';
/**
 * The visual vocabulary: what a step is, reduced to the handful of distinctions a drawing can carry
 * at 64x46 px. Coarser than `actionType` on purpose — thirty-five types cannot be told apart in a
 * thumbnail, but "this starts on a schedule, loops over something, and branches twice" can.
 *
 * ORDER IS PART OF THE WIRE FORMAT: `encodeFlowShape` writes the INDEX into this array. Append new
 * glyphs at the end and bump the codec version; never reorder or remove.
 */
export const FLOW_GLYPHS = [
    'start',
    'schedule',
    'signal',
    'act',
    'branch',
    'loop',
    'choose',
    'group',
    'compute',
    'store',
    'pause',
    'stop',
    'handoff',
];
/**
 * Which broad system a connector call touches. Also coarse by necessity: a tile cannot show fifty
 * connector brand logos, but it can show that a flow moves between mail, files and records.
 *
 * ORDER IS PART OF THE WIRE FORMAT — see `FLOW_GLYPHS`. `other` is first so it is index 0, the
 * value an absent or unrecognised connector encodes to.
 */
export const CONNECTOR_FAMILIES = [
    'other',
    'mail',
    'chat',
    'files',
    'records',
    'data',
    'web',
];
/**
 * How many steps get re-nested and drawn. Far below the walker's own `MAX_COMPONENTS` of 500, because
 * this bound is about a PICTURE, not about parsing: past a few dozen marks a tile is texture, and the
 * honest way to render step 300 is the "+N" overflow marker, not a 300th invisible dot.
 */
export const MAX_PREVIEW_NODES = 64;
/* ------------------------------------------------------------------------------------------------
 * Seeding
 *
 * FNV-1a and mulberry32, written out here rather than imported. `shared` may not import from `api`
 * (see index.ts), so `api/dataverse/components.ts`'s `fingerprintComponents` — which is also FNV-1a —
 * is a SIBLING, not a source. That separation is deliberate and not duplication by accident: that
 * digest is stored in `tool.component_docs_fingerprint` behind a CHECK constraint to invalidate stale
 * drafts. If the art seeded off it, a future change to what that digest hashes would silently
 * re-style every tile in the catalogue as a side effect of a draft-invalidation fix.
 * ---------------------------------------------------------------------------------------------- */
/**
 * FNV-1a 32-bit over the given parts, separated so `('a','bc')` and `('ab','c')` differ.
 * Non-ASCII code points fold both bytes, so a tool named in any script still seeds stably.
 */
export function flowSeed(...parts) {
    let h = 0x811c9dc5;
    for (const part of parts) {
        for (let i = 0; i < part.length; i += 1) {
            const c = part.charCodeAt(i);
            h = Math.imul(h ^ (c & 0xff), 0x01000193);
            if (c > 0xff)
                h = Math.imul(h ^ (c >>> 8), 0x01000193);
        }
        h = Math.imul(h ^ 0x1f, 0x01000193);
    }
    return h >>> 0;
}
/** mulberry32 — small, fast, well-distributed. Deterministic for a given seed, by contract. */
export function makeFlowRng(seed) {
    let a = seed >>> 0;
    return () => {
        a = (a + 0x6d2b79f5) >>> 0;
        let t = a;
        t = Math.imul(t ^ (t >>> 15), t | 1);
        t ^= t + Math.imul(t ^ (t >>> 7), t | 61);
        return ((t ^ (t >>> 14)) >>> 0) / 4294967296;
    };
}
/** Normalise a name for seeding, so casing and spacing changes alone do not re-style a tool. */
export function normaliseArtName(name) {
    return name.trim().toLowerCase().replace(/\s+/g, ' ');
}
/* ------------------------------------------------------------------------------------------------
 * Classification
 * ---------------------------------------------------------------------------------------------- */
/**
 * `actionType` -> glyph. The keys mirror `TYPE_LABELS` in `api/dataverse/components.ts`; that table
 * and this one are two halves of the same vocabulary, and a comment in each names the other. The
 * shared test asserts every `TYPE_LABELS` key resolves here, which is what stops them diverging.
 *
 * An unknown type is NOT an error and never disappears — it falls back to `act`, the generic "this
 * step does something" mark, for the same reason the walker's `humaniseType` exists: a step the
 * picture drops is a step the adopter is not told about.
 */
const GLYPH_BY_TYPE = {
    // Controls — the shapes that make a flow more than a list.
    If: 'branch',
    Switch: 'choose',
    Foreach: 'loop',
    Until: 'loop',
    Scope: 'group',
    // Starts.
    Recurrence: 'schedule',
    Request: 'signal',
    OpenApiConnectionWebhook: 'signal',
    OpenApiConnectionNotification: 'signal',
    ApiConnectionWebhook: 'signal',
    // Connector and network calls — the generic "does something" mark.
    OpenApiConnection: 'act',
    ApiConnection: 'act',
    Http: 'act',
    // Data shaping.
    Compose: 'compute',
    ParseJson: 'compute',
    Select: 'compute',
    Query: 'compute',
    Join: 'compute',
    Table: 'compute',
    Expression: 'compute',
    JavaScriptCode: 'compute',
    Xslt: 'compute',
    // Variables.
    InitializeVariable: 'store',
    SetVariable: 'store',
    IncrementVariable: 'store',
    DecrementVariable: 'store',
    AppendToArrayVariable: 'store',
    AppendToStringVariable: 'store',
    // Flow control that is not nesting.
    Wait: 'pause',
    Terminate: 'stop',
    Response: 'stop',
    Workflow: 'handoff',
};
/**
 * The glyph for one step. `kind === 'trigger'` is authoritative about STARTING the flow, so a trigger
 * whose type carries no start-shaped glyph of its own is drawn as `start` rather than as a plain
 * action — otherwise a flow could render with no visible beginning.
 */
export function glyphFor(actionType, kind) {
    const glyph = GLYPH_BY_TYPE[actionType] ?? 'act';
    if (kind === 'trigger' && glyph !== 'schedule' && glyph !== 'signal')
        return 'start';
    return glyph;
}
/**
 * The apps a step can be drawn AS, rather than merely categorised into.
 *
 * WHY THIS EXISTS: `family` is seven buckets, and `GLYPH_BY_TYPE` maps every connector call —
 * `OpenApiConnection`, whatever it talks to — to the single `act` glyph. So before this table, a flow
 * that mailed a person, filed a document and updated a row drew three identical circles. The picture
 * was varied without being informative, which is the failure this table fixes.
 *
 * ORDER IS PART OF THE WIRE FORMAT: `encodeFlowShape` writes the INDEX into this array (offset by one,
 * so 0 stays "no app"). Append new apps at the end; never reorder or remove one. 35 slots are
 * available at one base36 character, so there is room for 34 apps before the codec has to change.
 */
export const FLOW_APPS = [
    'outlook',
    'sharepoint',
    'teams',
    'dataverse',
    'excel',
    'onedrive',
    'sql',
    'forms',
    'planner',
    'todo',
    'approvals',
    'onenote',
    'directory',
    'salesforce',
    'powerbi',
    'dropbox',
    'blob',
    'gmail',
    'slack',
    'entra',
];
/**
 * What each app contributes to a drawing: a family, a monogram, and the name of a metaphor icon.
 *
 * `letter` is one or two characters and is NOT the app's brand name — it is a monogram, drawn where a
 * mark is too small for an icon but big enough for type. `icon` is a KEY, never a path: the path data
 * lives in `web/src/design/fluentPaths.ts` because it is drawing data, and keeping it out of `shared`
 * keeps this module free of anything a browser has to download to reason about a flow.
 *
 * `family` here WINS over `FAMILY_PATTERNS` (see `familyFor`), because a name we recognise exactly is
 * better evidence than a substring that happened to match. `onenote` is the case that proves it: no
 * pattern claims it, so without this table it would fall to `other` despite plainly being files.
 */
export const FLOW_APP_META = {
    outlook: { name: 'Outlook', letter: 'O', family: 'mail', icon: 'mail' },
    sharepoint: { name: 'SharePoint', letter: 'SP', family: 'files', icon: 'folder' },
    teams: { name: 'Teams', letter: 'T', family: 'chat', icon: 'team' },
    dataverse: { name: 'Dataverse', letter: 'DV', family: 'records', icon: 'database' },
    excel: { name: 'Excel', letter: 'X', family: 'data', icon: 'table' },
    onedrive: { name: 'OneDrive', letter: 'OD', family: 'files', icon: 'cloud' },
    sql: { name: 'SQL Server', letter: 'SQ', family: 'records', icon: 'database' },
    forms: { name: 'Forms', letter: 'F', family: 'data', icon: 'form' },
    planner: { name: 'Planner', letter: 'PL', family: 'records', icon: 'board' },
    todo: { name: 'To Do', letter: 'TD', family: 'records', icon: 'approve' },
    approvals: { name: 'Approvals', letter: 'A', family: 'records', icon: 'approve' },
    onenote: { name: 'OneNote', letter: 'ON', family: 'files', icon: 'notebook' },
    directory: { name: 'Directory', letter: 'U', family: 'records', icon: 'person' },
    salesforce: { name: 'Salesforce', letter: 'SF', family: 'records', icon: 'cloud' },
    powerbi: { name: 'Power BI', letter: 'BI', family: 'data', icon: 'chart' },
    dropbox: { name: 'Dropbox', letter: 'DB', family: 'files', icon: 'cloud' },
    blob: { name: 'Blob storage', letter: 'AB', family: 'files', icon: 'document' },
    gmail: { name: 'Gmail', letter: 'GM', family: 'mail', icon: 'mail' },
    slack: { name: 'Slack', letter: 'SL', family: 'chat', icon: 'chat' },
    entra: { name: 'Entra ID', letter: 'ID', family: 'web', icon: 'person' },
};
/**
 * Connector slug -> app. EXACT keys, and that is a security property rather than a style choice.
 *
 * `api/dataverse/components.ts`'s `connectorOf` has a third branch that falls back to a connection
 * REFERENCE's logical name, which is author-controlled — `new_sharedoffice365_8c3de` is a legitimate
 * value. A substring match on `office365` would resolve that to Outlook and, worse, would mean an
 * author-chosen string had steered a mark that ships to every viewer of a public catalogue. Matching
 * the whole normalised slug against this closed list means only the twenty strings BELOW can ever
 * select an app, so nothing an author can name is able to reach the drawing. The shared test pins
 * this with `shared_office365_secret_tenant_name`, which must resolve to no app at all.
 */
const APP_BY_SLUG = {
    office365: 'outlook',
    outlook: 'outlook',
    sharepointonline: 'sharepoint',
    teams: 'teams',
    commondataservice: 'dataverse',
    commondataserviceforapps: 'dataverse',
    dataverse: 'dataverse',
    excelonlinebusiness: 'excel',
    excelonline: 'excel',
    onedriveforbusiness: 'onedrive',
    onedrive: 'onedrive',
    sql: 'sql',
    microsoftforms: 'forms',
    planner: 'planner',
    todo: 'todo',
    approvals: 'approvals',
    onenote: 'onenote',
    office365users: 'directory',
    salesforce: 'salesforce',
    powerbi: 'powerbi',
    dropbox: 'dropbox',
    azureblob: 'blob',
    gmail: 'gmail',
    slack: 'slack',
    azuread: 'entra',
};
/** Normalise a connector API name to the slug `APP_BY_SLUG` is keyed on. */
function connectorSlug(connector) {
    return connector.toLowerCase().replace(/^shared_?/, '');
}
/**
 * Which enumerated app a connector names, or `null` when it names none.
 *
 * Exact-match by design — see `APP_BY_SLUG`. A `null` result is the ordinary case for the long tail
 * of connectors and for every control step, and the drawing degrades to the family mark rather than
 * guessing.
 */
export function appFor(connector) {
    if (!connector)
        return null;
    return APP_BY_SLUG[connectorSlug(connector)] ?? null;
}
/**
 * Connector API name -> family. First match wins, so specific patterns come first:
 * `office365users` is a directory lookup, not mail, and must be tested before `office365`.
 */
const FAMILY_PATTERNS = [
    [
        'records',
        /office365users|dataverse|commondataservice|dynamics|salesforce|sql|oracle|postgres|mysql|sap/,
    ],
    ['mail', /office365|outlook|gmail|sendgrid|smtp|mailchimp|mail/],
    ['chat', /teams|slack|twilio|sms|yammer|viva|webex|discord/],
    ['files', /sharepoint|onedrive|dropbox|googledrive|box|ftp|filesystem|azureblob|azurefile/],
    ['data', /excel|table|cosmos|powerbi|forms|dataflow|storage|redis|kusto/],
    ['web', /http|rss|webcontent|apim|graph|azuread|function/],
];
/**
 * Which family a connector belongs to. A null connector (a control, a compose) is `other`.
 *
 * The exact app table is consulted FIRST, then the patterns. Both orderings agree on every connector
 * either one recognises, which was checked before this call was made — the exact table only ever
 * decides cases the patterns get wrong or miss (`onenote` is claimed by no pattern and would fall to
 * `other`). The patterns stay as the long tail: they still catch `dynamics`, `sendgrid`, `cosmos` and
 * the hundreds of connectors that will never be enumerated. Keeping this in one function is what
 * makes the drawn `family` and the ENCODED family char agree, since the codec calls it too.
 */
export function familyFor(connector) {
    if (!connector)
        return 'other';
    const app = appFor(connector);
    if (app)
        return FLOW_APP_META[app].family;
    const name = connectorSlug(connector);
    for (const [family, pattern] of FAMILY_PATTERNS) {
        if (pattern.test(name))
            return family;
    }
    return 'other';
}
/**
 * Partition a control's children by branch, preserving first-appearance order.
 *
 * A Map keyed on the branch token does the grouping; `null` gets its own bucket rather than being
 * dropped, because `Scope` legitimately has unbranched children and they still need a group to be
 * laid out in.
 */
function groupByBranch(children) {
    const buckets = new Map();
    for (const child of children) {
        const key = child.branch ?? '\u0000null';
        const existing = buckets.get(key);
        if (existing)
            existing.nodes.push(child);
        else
            buckets.set(key, { branch: child.branch, nodes: [child] });
    }
    return [...buckets.values()].map(({ branch, nodes }) => ({
        branch,
        nodes,
        leafCount: nodes.reduce((sum, n) => sum + n.leafCount, 0),
    }));
}
/**
 * Measure `subtreeSize` and `leafCount` bottom-up, and build each control's branch groups.
 *
 * Iterative rather than recursive: `MAX_PREVIEW_NODES` makes recursion safe in practice, but this
 * function also runs over a decoded string whose depth column is attacker-adjacent, and an explicit
 * stack cannot be made to blow the call stack by a crafted input.
 */
function measure(roots) {
    const order = [];
    const stack = [...roots];
    while (stack.length) {
        const node = stack.pop();
        order.push(node);
        for (const child of node.children)
            stack.push(child);
    }
    // `order` is parents-before-children, so walking it backwards is children-before-parents.
    for (let i = order.length - 1; i >= 0; i -= 1) {
        const node = order[i];
        if (node.children.length === 0) {
            node.subtreeSize = 1;
            node.leafCount = 1;
            continue;
        }
        let size = 1;
        let leaves = 0;
        for (let j = 0; j < node.children.length; j += 1) {
            const child = node.children[j];
            child.siblingIndex = j;
            child.siblingCount = node.children.length;
            size += child.subtreeSize;
            leaves += child.leafCount;
        }
        node.subtreeSize = size;
        node.leafCount = Math.max(1, leaves);
        node.branchGroups = groupByBranch(node.children);
    }
}
/**
 * Re-nest a flat, depth-first step list into a measured tree.
 *
 * PARENTAGE IS RESOLVED TWO WAYS, in this order:
 *  1. `parentId`, when it names a step already seen. Depth-first order guarantees a real parent comes
 *     first, so a forward or dangling reference is by definition not resolvable and falls through.
 *  2. The depth stack — the nearest still-open ancestor shallower than this step.
 *
 * The fallback is not defensive padding. `flowComponents.test.ts` deliberately constructs "a list
 * that starts at depth 2", and `FlowComponentDoc` rows are stored per tool and read back
 * independently, so a list arriving with `parentId: null` throughout is a shape this has to draw
 * something sensible for rather than throw on.
 */
export function buildFlowPreview(nodes, opts) {
    const maxNodes = Math.max(1, opts?.maxNodes ?? MAX_PREVIEW_NODES);
    const sorted = [...nodes].sort((a, b) => a.order - b.order);
    const kept = sorted.slice(0, maxNodes);
    const roots = [];
    const byId = new Map();
    /** `openAt[d]` = the most recent node whose normalised depth is `d`. */
    const openAt = [];
    for (const source of kept) {
        const declared = Number.isFinite(source.depth) ? Math.max(0, Math.floor(source.depth)) : 0;
        let parent = null;
        if (source.parentId)
            parent = byId.get(source.parentId) ?? null;
        if (!parent && declared > 0) {
            for (let d = Math.min(declared - 1, openAt.length - 1); d >= 0; d -= 1) {
                const candidate = openAt[d];
                if (candidate) {
                    parent = candidate;
                    break;
                }
            }
        }
        const node = {
            id: source.id,
            order: source.order,
            // Normalised, not declared: the tree is the authority. See FlowPreviewNode.depth.
            depth: parent ? parent.depth + 1 : 0,
            kind: source.kind,
            glyph: glyphFor(source.actionType, source.kind),
            family: familyFor(source.connector),
            app: appFor(source.connector),
            branch: parent ? source.branch : null,
            siblingIndex: 0,
            siblingCount: 1,
            subtreeSize: 1,
            leafCount: 1,
            children: [],
            branchGroups: [],
        };
        if (parent)
            parent.children.push(node);
        else
            roots.push(node);
        byId.set(node.id, node);
        openAt.length = node.depth + 1;
        openAt[node.depth] = node;
    }
    for (let i = 0; i < roots.length; i += 1) {
        roots[i].siblingIndex = i;
        roots[i].siblingCount = roots.length;
    }
    measure(roots);
    return {
        fidelity: 'structural',
        roots,
        stats: statsFor(kept, byId, sorted.length),
    };
}
/** Whole-flow measurements over the kept nodes, with `stepCount` from the untruncated total. */
function statsFor(kept, byId, totalCount) {
    const kindCounts = Object.fromEntries(FLOW_COMPONENT_KINDS.map((k) => [k, 0]));
    const glyphCounts = {};
    const familySet = new Set();
    const appSet = new Set();
    const connectors = new Set();
    let maxDepth = 0;
    for (const source of kept) {
        const node = byId.get(source.id);
        if (!node)
            continue;
        kindCounts[node.kind] += 1;
        glyphCounts[node.glyph] = (glyphCounts[node.glyph] ?? 0) + 1;
        familySet.add(node.family);
        if (node.app)
            appSet.add(node.app);
        if (source.connector)
            connectors.add(source.connector.toLowerCase());
        if (node.depth > maxDepth)
            maxDepth = node.depth;
    }
    return {
        stepCount: totalCount,
        drawnCount: kept.length,
        maxDepth,
        branchCount: (glyphCounts.branch ?? 0) + (glyphCounts.choose ?? 0),
        loopCount: glyphCounts.loop ?? 0,
        kindCounts,
        glyphCounts,
        families: CONNECTOR_FAMILIES.filter((f) => familySet.has(f)),
        apps: FLOW_APPS.filter((a) => appSet.has(a)),
        connectorCount: connectors.size,
        truncated: totalCount > kept.length,
    };
}
/**
 * The emblem model — an identity mark for a tool whose structure is unavailable.
 *
 * `pipsAreKnown` matters more than it looks: when a surface really knows the connector count, the
 * pips are a fact and are drawn as filled pads; when it does not, they are seeded decoration and are
 * drawn as open ticks instead. Without that distinction a viewer would read invented pips as a count.
 */
export function buildFlowEmblem(input) {
    const rng = makeFlowRng(input.seed);
    const known = typeof input.connectorCount === 'number' && input.connectorCount >= 0;
    return {
        fidelity: 'emblem',
        known: {
            connectorCount: known ? input.connectorCount : null,
            categoryLabel: input.categoryLabel ?? null,
            hasFlow: input.hasFlow ?? false,
        },
        // 2..4 arcs. Never one arc per step — there is no step count here, and implying one would be the
        // exact dishonesty this model exists to avoid.
        arcCount: 2 + Math.floor(rng() * 3),
        pipCount: known ? Math.min(8, input.connectorCount) : 3 + Math.floor(rng() * 3),
        pipsAreKnown: known,
        markSeed: input.seed >>> 0,
    };
}
/* ------------------------------------------------------------------------------------------------
 * Colour selection
 * ---------------------------------------------------------------------------------------------- */
/**
 * The hue offset in STEPS, -3..+3. Deliberately an integer and deliberately the only colour decision
 * made in JavaScript: `flowArt.css` multiplies it by `--art-hue-step` and composes the `hsl()`, so no
 * colour literal of any kind appears in a `.tsx` file. That keeps every hue inside the token system
 * and keeps `audit-tokens.mjs` satisfied without an exemption.
 */
export function pickHueStep(hueSeed) {
    return ((hueSeed >>> 5) % 7) - 3;
}
/* ------------------------------------------------------------------------------------------------
 * Wire codec
 *
 * `v2:` then FOUR base36 characters per step. The depth-first ORDER of the list plus each step's
 * depth is enough to rebuild the tree exactly — a DFS sequence of depths has one and only one
 * parse — so this is a LOSSLESS encoding of everything the art draws, not a lossy summary. At four
 * characters a step, a 20-step flow is 83 bytes.
 *
 *   char 0  depth, clamped to 0..35
 *   char 1  index into FLOW_GLYPHS
 *   char 2  branch slot and connector family, packed: slot * 7 + family.
 *           slot 0 = unbranched, 1..4 = branch index + 1 (capped). 5 slots x 7 families = 35 <= 36.
 *   char 3  index into FLOW_APPS, PLUS ONE, so 0 means "no enumerated app". 20 apps -> max 'k'.
 *
 * WHAT IS DELIBERATELY NOT WRITTEN: the connector STRING. `api/dataverse/components.ts`'s
 * `connectorOf` can fall back to an author-named connection reference, so a raw connector name is
 * author-controlled text and this value is served to every viewer of a public catalogue. An INDEX into
 * a closed twenty-entry table cannot carry anything an author wrote, which is what makes per-app
 * identity safe to put on the wire at all. Same reason branch LABELS become slot numbers.
 *
 * VERSIONING. `v1:` is still decoded, at its own stride of 3 and with `app` absent — a persisted
 * string from an earlier build must degrade by one field, not fail to a blank plate. Add fields by
 * bumping to `v3:` and widening the stride; never by changing what an existing char means.
 * ---------------------------------------------------------------------------------------------- */
const CODEC_VERSION = 'v2';
/** Strides of every version this build can READ, keyed by prefix. */
const CODEC_STRIDES = { 'v1:': 3, 'v2:': 4 };
const MAX_CODEC_DEPTH = 35;
const BRANCH_SLOTS = 5;
/**
 * Encode a step list. Branch tokens become SLOT INDICES, numbered per parent in first-appearance
 * order — the label text itself is never written, which keeps the disclosure boundary intact through
 * the codec as well as through `FlowShapeNode`.
 */
export function encodeFlowShape(nodes, opts) {
    const maxNodes = Math.max(1, opts?.maxNodes ?? MAX_PREVIEW_NODES);
    const kept = [...nodes].sort((a, b) => a.order - b.order).slice(0, maxNodes);
    /** Branch tokens seen under each parent id, so a slot index is stable within a parent. */
    const slotsByParent = new Map();
    let out = `${CODEC_VERSION}:`;
    for (const node of kept) {
        const depth = Math.min(MAX_CODEC_DEPTH, Number.isFinite(node.depth) ? Math.max(0, Math.floor(node.depth)) : 0);
        const glyphIndex = FLOW_GLYPHS.indexOf(glyphFor(node.actionType, node.kind));
        const familyIndex = CONNECTOR_FAMILIES.indexOf(familyFor(node.connector));
        // +1 so that 0 is unambiguously "no app". `appFor` is exact-match, so this index can only ever
        // name one of the twenty enumerated apps.
        const app = appFor(node.connector);
        const appIndex = app ? FLOW_APPS.indexOf(app) + 1 : 0;
        let slot = 0;
        if (node.branch) {
            const parentKey = node.parentId ?? `\u0000d${depth}`;
            const seen = slotsByParent.get(parentKey) ?? [];
            if (!slotsByParent.has(parentKey))
                slotsByParent.set(parentKey, seen);
            let index = seen.indexOf(node.branch);
            if (index === -1) {
                seen.push(node.branch);
                index = seen.length - 1;
            }
            slot = Math.min(BRANCH_SLOTS - 1, index + 1);
        }
        out +=
            depth.toString(36) +
                Math.max(0, glyphIndex).toString(36) +
                (slot * CONNECTOR_FAMILIES.length + Math.max(0, familyIndex)).toString(36) +
                appIndex.toString(36);
    }
    return out;
}
/**
 * Decode a shape string, or `null` on ANY malformed input — wrong prefix, wrong length, a character
 * outside base36, a glyph index past the end of the table.
 *
 * Returning null rather than throwing mirrors `skinStore.ts`'s `parseSkin`: this value can arrive from
 * a stored payload written by an older or newer build, and the caller's correct response is to fall
 * back to the emblem. A decorative graphic must never be able to break a page.
 *
 * The `branch` values it produces are OPAQUE SLOT TOKENS (`b1`, `b2`), not the author's branch names,
 * which the codec never carried. They exist only to group siblings and are never rendered as text.
 */
export function decodeFlowShape(code) {
    if (typeof code !== 'string')
        return null;
    const prefix = code.slice(0, 3);
    const stride = CODEC_STRIDES[prefix];
    if (!stride)
        return null;
    const body = code.slice(prefix.length);
    if (body.length % stride !== 0)
        return null;
    if (body.length / stride > MAX_PREVIEW_NODES)
        return null;
    const out = [];
    /** The open ancestor at each depth, so `parentId` can be rebuilt from the depth column alone. */
    const openAt = [];
    for (let i = 0; i < body.length; i += stride) {
        const depth = parseInt(body[i], 36);
        const glyphIndex = parseInt(body[i + 1], 36);
        const packed = parseInt(body[i + 2], 36);
        if (!Number.isInteger(depth) || !Number.isInteger(glyphIndex) || !Number.isInteger(packed)) {
            return null;
        }
        if (glyphIndex >= FLOW_GLYPHS.length)
            return null;
        const slot = Math.floor(packed / CONNECTOR_FAMILIES.length);
        const familyIndex = packed % CONNECTOR_FAMILIES.length;
        if (slot >= BRANCH_SLOTS)
            return null;
        // `v1:` has no app column at all, which is why the stride is per-version rather than fixed.
        let app = null;
        if (stride > 3) {
            const appIndex = parseInt(body[i + 3], 36);
            if (!Number.isInteger(appIndex))
                return null;
            if (appIndex > FLOW_APPS.length)
                return null;
            app = appIndex === 0 ? null : FLOW_APPS[appIndex - 1];
        }
        const order = out.length;
        const id = `s${order}`;
        const parentId = depth > 0 ? (openAt[depth - 1] ?? null) : null;
        const glyph = FLOW_GLYPHS[glyphIndex];
        out.push({
            id,
            order,
            depth,
            parentId,
            kind: glyphKind(glyph, order),
            // Round-trips to the same glyph, family and app, which is all the art reads. See the note above.
            actionType: representativeType(glyph),
            connector: representativeConnector(CONNECTOR_FAMILIES[familyIndex], app),
            branch: slot === 0 ? null : `b${slot}`,
        });
        openAt.length = depth + 1;
        openAt[depth] = id;
    }
    return out;
}
/** The kind a decoded glyph implies. Only the first step may be a trigger. */
function glyphKind(glyph, order) {
    if (order === 0 && (glyph === 'start' || glyph === 'schedule' || glyph === 'signal')) {
        return 'trigger';
    }
    if (glyph === 'branch' || glyph === 'loop' || glyph === 'choose' || glyph === 'group') {
        return 'control';
    }
    return 'action';
}
/**
 * One `actionType` that maps back to each glyph, so a decoded node re-classifies identically through
 * `glyphFor`. The choice within a glyph is arbitrary and never shown — `Compose` stands for all of
 * `compute`, not for itself.
 */
function representativeType(glyph) {
    switch (glyph) {
        case 'schedule':
            return 'Recurrence';
        case 'signal':
            return 'Request';
        case 'branch':
            return 'If';
        case 'loop':
            return 'Foreach';
        case 'choose':
            return 'Switch';
        case 'group':
            return 'Scope';
        case 'compute':
            return 'Compose';
        case 'store':
            return 'SetVariable';
        case 'pause':
            return 'Wait';
        case 'stop':
            return 'Terminate';
        case 'handoff':
            return 'Workflow';
        case 'start':
        case 'act':
        default:
            return 'OpenApiConnection';
    }
}
/**
 * The canonical slug for each app — the FIRST key in `APP_BY_SLUG` that names it.
 *
 * Derived rather than declared so there is exactly one place an app's slugs are written. String keys
 * iterate in insertion order, and `APP_BY_SLUG` lists each app's canonical name first, so this is
 * stable. Used only to rebuild a connector on decode.
 */
const SLUG_BY_APP = (() => {
    const out = {};
    for (const [slug, app] of Object.entries(APP_BY_SLUG)) {
        if (out[app] === undefined)
            out[app] = slug;
    }
    return out;
})();
/**
 * One connector name that re-classifies to the given family and app, so a decoded node produces the
 * same drawing as the node it was encoded from. The sibling of `representativeType`, and it stands for
 * its family rather than for itself: `oracle` means "some records system", not Oracle.
 *
 * The family representatives are deliberately NOT any of the twenty app slugs, so a step encoded with
 * a family but no app cannot come back out of the codec wearing an app's mark. This function is the
 * only reason `familyFor`'s pattern list has to keep matching these seven names.
 */
function representativeConnector(family, app) {
    if (app)
        return SLUG_BY_APP[app];
    switch (family) {
        case 'mail':
            return 'smtp';
        case 'chat':
            return 'twilio';
        case 'files':
            return 'ftp';
        case 'records':
            return 'oracle';
        case 'data':
            return 'cosmos';
        case 'web':
            return 'http';
        case 'other':
        default:
            // A control step, a compose, a connector we could not classify: genuinely no connector.
            return null;
    }
}
/**
 * A one-sentence description of a flow's shape, for `aria-label` on the one surface where the art is
 * meaningful rather than ornamental (the upload wizard preview).
 *
 * Generated from `stats` only — never from labels, never from author text. A screen reader gets the
 * same facts a sighted reader takes from the picture, which is the whole test for whether a generated
 * graphic is accessible or just decorative.
 */
export function describeFlowShape(stats) {
    const parts = [`${stats.stepCount} ${stats.stepCount === 1 ? 'step' : 'steps'}`];
    if (stats.maxDepth > 0) {
        parts.push(`${stats.maxDepth + 1} nesting levels`);
    }
    if (stats.branchCount > 0) {
        parts.push(`${stats.branchCount} ${stats.branchCount === 1 ? 'condition' : 'conditions'}`);
    }
    if (stats.loopCount > 0) {
        parts.push(`${stats.loopCount} ${stats.loopCount === 1 ? 'loop' : 'loops'}`);
    }
    if (stats.connectorCount > 0) {
        parts.push(`${stats.connectorCount} ${stats.connectorCount === 1 ? 'connector' : 'connectors'}`);
    }
    // The apps are named because the DRAWING names them — a sighted reader sees the Outlook and
    // SharePoint marks, so a screen reader has to be told the same thing or the picture is carrying a
    // fact the label withholds. Product names only, from the closed table; never a connector string.
    if (stats.apps.length > 0) {
        parts.push(`using ${stats.apps.map((a) => FLOW_APP_META[a].name).join(', ')}`);
    }
    return `Flow shape: ${parts.join(', ')}.`;
}
//# sourceMappingURL=flowPreview.js.map